"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/result/charts/IdleCashYieldChart";

import type { FC } from "react";
import { cn } from "utils";
import {
  formatResultCompactCurrency,
  formatResultSignedCompactCurrency,
} from "../formatResultCurrency";
import type { IdleCashGrowthChartPayload } from "../types";

const I18n = I18nUtil.createScoped(Translations);

interface IdleCashYieldChartProps {
  data: IdleCashGrowthChartPayload;
}

const BAR_COUNT = 36;
const MAX_BAR_HEIGHT = 130;

interface BarSegment {
  orangeHeight: number;
  tealHeight: number;
}

function buildBarSegments(
  currentYield: number,
  withEdYield: number,
  years: number,
): BarSegment[] {
  const maxEd = Math.pow(1 + withEdYield / 100, years);

  return Array.from({ length: BAR_COUNT }, (_, index) => {
    const progress = index / (BAR_COUNT - 1);
    const elapsedYears = progress * years;
    const ownMultiplier = Math.pow(1 + currentYield / 100, elapsedYears);
    const edMultiplier = Math.pow(1 + withEdYield / 100, elapsedYears);
    const totalHeight = (edMultiplier / maxEd) * MAX_BAR_HEIGHT;
    const orangeHeight = Math.min(
      totalHeight,
      (ownMultiplier / maxEd) * MAX_BAR_HEIGHT,
    );
    const tealHeight = Math.max(0, totalHeight - orangeHeight);

    return { orangeHeight, tealHeight };
  });
}

function LegendRow({
  dotClassName,
  label,
  value,
}: {
  dotClassName: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn("size-2 shrink-0 rounded-full", dotClassName)} />
      <p className="body_medium_s text-content_secondary">{label}</p>
      <span className="min-w-0 flex-1" />
      <p className="body_bold_m text-content_primary">{value}</p>
    </div>
  );
}

export const IdleCashYieldChart: FC<IdleCashYieldChartProps> = ({ data }) => {
  const bars = buildBarSegments(
    data.currentYield,
    data.withEdYield,
    data.longTermYears,
  );
  const onYourOwnTotal =
    data.idleCashAmount *
    Math.pow(1 + data.currentYield / 100, data.longTermYears);

  return (
    <div className="w-full" data-testid="result-yield-chart">
      <div className="relative mx-auto h-[198px] w-full max-w-[313px]">
        <div
          className="absolute inset-x-0 top-0 flex h-[176px] items-end gap-[5px]"
          data-testid="result-idle-cash-bars"
        >
          {bars.map((bar, index) => (
            <div
              key={index}
              className="flex w-1 flex-col justify-end"
              style={{ height: MAX_BAR_HEIGHT }}
            >
              {bar.tealHeight > 0 ? (
                <div
                  className="w-full rounded-full bg-content_brand_primary"
                  style={{ height: `${bar.tealHeight}px` }}
                />
              ) : null}
              {bar.orangeHeight > 0 ? (
                <div
                  className="w-full rounded-full bg-background_accent4_overlay100"
                  style={{ height: `${bar.orangeHeight}px` }}
                />
              ) : null}
            </div>
          ))}
        </div>

        <div className="body_medium_s absolute inset-x-0 top-[182px] flex justify-between text-content_tertiary">
          <span>{I18n.t("Today")}</span>
          <span>{data.midYear}</span>
          <span>{data.endYear}</span>
        </div>

        <div className="absolute left-[58%] top-1 -translate-x-1/2 rounded bg-content_brand_primary px-2.5 py-1.5">
          <p className="body_bold_s whitespace-nowrap text-content_inverse_primary">
            {`${formatResultCompactCurrency(data.longTermGain)} extra with Ed`}
          </p>
        </div>

        <span
          className="absolute left-[calc(100%-12px)] top-10 size-5 rounded-full border-2 border-content_brand_primary bg-background_solid_neutral_primary"
          data-testid="result-idle-cash-chart-end-dot"
        />
      </div>
      <div
        className="flex flex-col gap-2 px-1 py-4"
        data-testid="result-chart-legend"
      >
        <LegendRow
          dotClassName="bg-content_brand_primary"
          label={`Extra with Ed · ${data.withEdYield}%`}
          value={formatResultSignedCompactCurrency(data.longTermGain)}
        />
        <LegendRow
          dotClassName="bg-background_accent4_overlay100"
          label={`On your own · ${data.currentYield}%`}
          value={formatResultCompactCurrency(onYourOwnTotal)}
        />
      </div>
    </div>
  );
};
